import { readOfflineDataset, saveOfflineDataset, type OfflineDatasetRecord } from '@/lib/offline/datasetCache';

export type OfflineDatasetKind = 'menu' | 'tables' | 'orders' | 'settings' | 'staff';
export const ALL_OFFLINE_DATASETS: OfflineDatasetKind[] = ['menu', 'tables', 'orders', 'settings', 'staff'];

export interface SnapshotResponse<T = unknown> {
  dataset: OfflineDatasetKind;
  version: string;
  data: T;
  generatedAt: number;
}

interface HydratorOptions {
  tenantSlug: string;
  datasets: OfflineDatasetKind[];
  intervalMs?: number;
}

export interface HydratorHandle {
  stop: () => void;
  flush: () => Promise<void>;
}

const fetchSnapshot = async <T>(
  tenantSlug: string,
  dataset: OfflineDatasetKind,
): Promise<SnapshotResponse<T>> => {
  const response = await fetch(`/api/offline/snapshots/${dataset}`, {
    headers: {
      Accept: 'application/json',
      'X-Tenant': tenantSlug,
    },
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Snapshot fetch failed for ${dataset} (${response.status})`);
  }
  const payload = (await response.json()) as SnapshotResponse<T>;
  return payload;
};

const hydrateDataset = async <T>(tenantSlug: string, dataset: OfflineDatasetKind): Promise<void> => {
  try {
    const snapshot = await fetchSnapshot<T>(tenantSlug, dataset);
    const record: OfflineDatasetRecord<T> = {
      tenantSlug,
      dataset,
      version: snapshot.version,
      data: snapshot.data,
      cachedAt: snapshot.generatedAt,
    };
    await saveOfflineDataset(record);
  } catch (error) {
    console.warn('[offline][hydrator] failed to hydrate dataset', { dataset, error });
  }
};

const hydrateAll = async (tenantSlug: string, datasets: OfflineDatasetKind[]): Promise<void> => {
  for (const dataset of datasets) {
    await hydrateDataset(tenantSlug, dataset);
  }
};

export const startOfflineDatasetHydrator = (options: HydratorOptions): HydratorHandle => {
  const intervalMs = options.intervalMs ?? 5 * 60_000;
  let timer: ReturnType<typeof setInterval> | null = null;

  void hydrateAll(options.tenantSlug, options.datasets);

  timer = setInterval(() => {
    void hydrateAll(options.tenantSlug, options.datasets);
  }, intervalMs);

  return {
    stop: () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    },
    flush: () => hydrateAll(options.tenantSlug, options.datasets),
  };
};

export const bootstrapOfflineDataset = async <T>(
  tenantSlug: string,
  dataset: OfflineDatasetKind,
): Promise<OfflineDatasetRecord<T> | null> => {
  return readOfflineDataset<T>(tenantSlug, dataset);
};
